import { Link } from 'react-router-dom';
import { bilanCohorte1 as bilan, projets } from '../data/siteData';
import useSeoMeta from '../hooks/useSeoMeta';
import './BilanCohorte1.css';

const YT_WATCH = id => `https://www.youtube.com/watch?v=${id}`;
const YT_THUMB = id => `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

function VideoCard({ video, wide }) {
  return (
    <a
      className={`bilan-video${wide ? ' bilan-video--wide' : ''}`}
      href={YT_WATCH(video.id)}
      target="_blank"
      rel="noopener noreferrer"
    >
      <div className="bilan-video__thumb">
        <img src={YT_THUMB(video.id)} alt="" loading="lazy" />
        <span className="bilan-video__play" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22"><path d="M8 5v14l11-7z" /></svg>
        </span>
      </div>
      <div className="bilan-video__body">
        <span className="bilan-video__title">{video.titre}</span>
        {video.desc && <span className="bilan-video__desc">{video.desc}</span>}
      </div>
    </a>
  );
}

function VideoGroup({ eyebrow, title, videos, wide }) {
  return (
    <div className="bilan-videogroup">
      <div className="section-eyebrow">{eyebrow}</div>
      <h3 className="bilan-videogroup__title">{title}</h3>
      <div className={`bilan-videogrid${wide ? ' bilan-videogrid--wide' : ''}`}>
        {videos.map(v => <VideoCard key={v.id} video={v} wide={wide} />)}
      </div>
    </div>
  );
}

export default function BilanCohorte1() {
  useSeoMeta(null, {
    title: `Bilan — ${bilan.titre} | Tech Talent Accelerator`,
    desc: `${bilan.periode} : 52 jeunes formés et certifiés aux métiers de l'IA générative et du Machine Learning à Abidjan.`,
  });

  const cohorte2 = projets.find(p => p.id === 'tta-cohorte-2');

  return (
    <main className="bilan">
      {/* ── HERO ── */}
      <section className="bilan-hero">
        <div className="bilan-hero__bg" />
        <div className="bilan-hero__inner">
          <div className="bilan-hero__badge">
            <span />Programme terminé · {bilan.periode}
          </div>
          <h1 className="bilan-hero__title">
            Bilan du programme<br />
            <span className="grad-text">{bilan.titre}</span>
          </h1>
          <p className="bilan-hero__intro">{bilan.intro}</p>

          <ul className="bilan-kpis">
            {bilan.chiffres.map(c => (
              <li key={c.label}>
                <strong>{c.valeur}</strong>
                <span className="bilan-kpis__label">{c.label}</span>
                <span className="bilan-kpis__detail">{c.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── PARCOURS ── */}
      <section className="section">
        <div className="section-eyebrow">Le parcours</div>
        <h2 className="section-title" style={{ fontSize: '32px' }}>Ce qu'ont suivi les apprenants</h2>
        <p className="section-subtitle">
          Dix semaines de bootcamp intensif, complétées par un accompagnement vers l'emploi et une certification vérifiable.
        </p>
        <div className="bilan-parcours">
          {bilan.parcours.map((p, i) => (
            <article key={p.titre} className="bilan-parcours__card">
              <span className="bilan-parcours__num">{String(i + 1).padStart(2, '0')}</span>
              <h3>{p.titre}</h3>
              <p>{p.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="section section-alt">
        <div className="section-eyebrow">Déroulé</div>
        <h2 className="section-title" style={{ fontSize: '32px' }}>Trois mois et demi, étape par étape</h2>
        <ol className="bilan-timeline">
          {bilan.timeline.map(t => (
            <li key={t.date}>
              <span className="bilan-timeline__date">{t.date}</span>
              <div className="bilan-timeline__body">
                <h3>{t.titre}</h3>
                <p>{t.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── VIDÉOS ── */}
      <section className="section">
        <div className="section-eyebrow">En images</div>
        <h2 className="section-title" style={{ fontSize: '32px' }}>Revivre le programme</h2>
        <p className="section-subtitle">
          Témoignages, compétition internationale et projets développés par les apprenants.
        </p>

        <VideoGroup
          eyebrow="Ils racontent"
          title="Témoignages d'alumni"
          videos={bilan.videos.temoignages}
          wide
        />
        <VideoGroup
          eyebrow="31 juillet 2026"
          title="Cérémonie de clôture"
          videos={bilan.videos.ceremonie}
          wide
        />
        <VideoGroup
          eyebrow="9 — 13 juillet 2026"
          title="Global AI Hackathon"
          videos={bilan.videos.hackathon}
        />
        <VideoGroup
          eyebrow="Demo Days"
          title="Quelques projets de la cohorte"
          videos={bilan.videos.projets}
        />

        <div className="bilan-chaine">
          <a href={bilan.chaineYoutube} target="_blank" rel="noopener noreferrer">
            Voir toutes les vidéos sur la chaîne YouTube du programme
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </a>
        </div>
      </section>

      {/* ── CTA COHORTE 2 ── */}
      {cohorte2 && (
        <section className="bilan-cta">
          <div className="bilan-cta__inner">
            <div>
              <div className="bilan-cta__eyebrow">La suite</div>
              <h2>Une nouvelle cohorte se prépare</h2>
              <p>
                Le dispositif reprend pour une deuxième promotion. Déposez votre candidature dès
                maintenant pour être parmi les premiers informés du démarrage.
              </p>
            </div>
            <div className="bilan-cta__actions">
              <a
                href={cohorte2.candidatureLink}
                target="_blank"
                rel="noopener noreferrer"
                className="bilan-cta__btn"
              >
                Candidater à la Cohorte 2
              </a>
              <Link to="/projets" className="bilan-cta__link">Voir tous nos projets</Link>
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
